import { renderableSlideIndices } from '@digital-twins/deck-ui/slides/slide-retirement'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  Loader2,
  ArrowLeft,
  Pencil,
  Undo2,
  Redo2,
  RotateCcw,
  Eye,
  EyeOff,
  Check,
  Download,
  Presentation,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { Button } from '@/components/ui/button'
import { HoverCardPortalProvider } from '@/components/ui/hover-card'
import { cn } from '@/lib/utils'
import type { SlideData, Deck } from '@digital-twins/deck-ui/slides/contracts'
import { SlideFieldEdit } from '@digital-twins/deck-ui/slides/contracts'
import {
  ChartSegmentProvider,
} from '@digital-twins/deck-ui/components/chart-segment'
import { deckPdfFilename, deckPptxFilename } from '@digital-twins/shared-types'
import type { ChartSegmentMeta, ChartSegmentView, ChartSegmentViewRequest, SampleDemographics } from '@digital-twins/shared-types'
import { SlideErrorBoundary, NoDataFallback } from '@digital-twins/deck-ui/slides/SlideErrorBoundary'
import { validateSlideData } from '@/lib/validation/slide-sanitizer'
import { deckApi, isGuestSessionExpiredError, publicReportBlob } from '@/lib/api/client'
import {
  buildFallbackChartSegmentMeta,
  mergeChartSegmentMeta,
} from '@/features/studies/report/chartSegmentFallback'
import {
  getSlideComponent,
  validateRegistryCoverage,
  ensureSlideRegistryLoaded,
} from '@digital-twins/deck-ui/slides/registry'
import { prepareDeckForRender } from '@/features/studies/deck/render/prepareDeckForRender'
import {
  FailedExhibit,
  reportViewState,
} from '@/features/studies/report/reportViewState'
import { buildDeckPagerEntries, deckPagerIndexOf, readerVisibleSlides } from './deckPager'
import { FailedSlidePlaceholder } from './FailedSlidePlaceholder'
import { applyDeckMetadataBranding, deckThemeTokensForExport } from '@/features/studies/deck/render/deckMetadataBranding'
import { useTheme } from '@/lib/theme-provider'
import { useDeckEditing } from './useDeckEditing'
import { useChartSegmentLabels, useSlideErrorBoundaryLabels } from './deckChromeLabels'
import slideDimensions from '../../../../../../../../../config/slide_dimensions.json'

// Note: Slide rendering uses getSlideComponent() from registry, not dynamic imports.
// Components self-register via registerSlide() when their module is loaded.

interface DeckViewerProps {
  deck: Deck
  onSlideChange?: (index: number) => void
  onEdit?: (slideIndex: number, field: string, value: unknown) => void
  editable?: boolean
  /** Optional callback when a slide persists edits and wants to update deck state */
  onDeckUpdate?: (deck: Deck) => void
  className?: string
  /** Callback when close/back button is clicked (for story mode) */
  onClose?: () => void
  /** Optional experiment name for PDF filename */
  experimentName?: string
  /** Optional seed to stabilize cover background selection */
  coverBackgroundSeed?: string
  /**
   * When true, the component fills its parent container instead of enforcing
   * aspect-video sizing. Used when DeckViewer is embedded inline in a tab.
   */
  embedded?: boolean
  /** Experiment token used to fetch chart-segment meta/views. */
  studyId?: string
  /** Used to show the gear when the meta API returns no axes. */
  sampleDemographics?: SampleDemographics | null
  /** Disable authenticated chart segmentation, such as for public reports. */
  chartSegmentationEnabled?: boolean
  /** Exhibits that failed in the accepted publication, shown as placeholder slides. */
  failedExhibits?: Array<FailedExhibit>
  /** The accepted slide sequence (item ids) that places each failed exhibit. */
  slideOrder?: Array<string>
  /** A public share-link view: a failed slide never offers Retry. */
  isPublicView?: boolean
  /** Retry offered on a failed slide (the page's report regeneration). */
  onRetryFailedExhibit?: () => void
  /** A retry is in flight. */
  isRetryingFailedExhibit?: boolean
  /** A sequence item to open at (a storyline exhibit link); applied once per mount and id. */
  focusSlideId?: string | null
  /**
   * Share token for a public report. When set, PDF export uses the guest
   * deck route instead of the signed-in deck route.
   */
  shareToken?: string
}

const NO_FAILED_EXHIBITS: Array<FailedExhibit> = []
const NO_SLIDE_ORDER: Array<string> = []

interface ChartSegmentViewCacheEntry {
  controller: AbortController
  promise: Promise<ChartSegmentView>
  consumers: number
  isSettled: boolean
  isAbortScheduled: boolean
}

const CHART_SEGMENT_VIEW_CACHE_LIMIT = 32

function useSlideRegistryDiagnostics() {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      return
    }
    function runCoverageCheck() {
      const { missing, extra } = validateRegistryCoverage()
      if (missing.length > 0) {
        console.warn('[SlideRegistry] Missing registered slide types:', missing)
      }
      if (extra.length > 0) {
        console.warn('[SlideRegistry] Extra registered slide types:', extra)
      }
    }
    void ensureSlideRegistryLoaded().then(runCoverageCheck)
  }, [])
}

function useSlideRegistryLoader() {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isCancelled = false
    setReady(false)
    setError(null)
    ensureSlideRegistryLoaded()
      .then(() => {
        if (!isCancelled) {
          setReady(true)
        }
      })
      .catch((err) => {
        if (!isCancelled) {
          setError((err as Error)?.message || String(err))
        }
      })
    return () => {
      isCancelled = true
    }
  }, [])

  return { ready, error }
}

// Slide renderer component (exported for use in TwoPanelSlide)
export function SlideRenderer({
  slide,
  isActive,
  onEdit,
  onEditFields,
  editable,
  experimentId,
  deck,
  onDeckUpdate,
}: {
  slide: SlideData
  isActive: boolean
  onEdit?: (field: string, value: unknown) => void
  /** Several fields from one user edit, recorded as one undo step. */
  onEditFields?: (edits: Array<SlideFieldEdit>) => void
  editable?: boolean
  experimentId?: string
  deck?: Deck
  onDeckUpdate?: (deck: Deck) => void
}) {
  const slideErrorLabels = useSlideErrorBoundaryLabels()
  const qualCreativeFilterLabels = {
    allCreatives: i18n.t('studyReport.qualCreativeFilter.allCreatives', { ns: 'app' }),
    triggerAriaLabel: i18n.t('studyReport.qualCreativeFilter.triggerAriaLabel', { ns: 'app' }),
    title: i18n.t('studyReport.qualCreativeFilter.title', { ns: 'app' }),
    explanation: i18n.t('studyReport.qualCreativeFilter.explanation', { ns: 'app' }),
    filterByAsset: i18n.t('studyReport.qualCreativeFilter.filterByAsset', { ns: 'app' }),
  }
  // Plots plan OT (decision 23): only the active slide opens a plot's "Other";
  // thumbnails and the collage keep the Other bar.
  const plotOtherToggleLabels = isActive
    ? {
        expand: (count: number) => i18n.t('studyReport.plotOther.expand', { ns: 'app', count }),
        collapse: i18n.t('studyReport.plotOther.collapse', { ns: 'app' }),
      }
    : undefined
  // The title of a stored bars slide's Other hover list (legacy reader), in
  // the viewer's UI language rather than a hardcoded English word.
  const plotOtherMembersHeading = i18n.t('studyReport.plotOther.includes', { ns: 'app' })
  const baseProps = {
    isActive,
    onEdit,
    onEditFields,
    editable,
    experimentId,
    deck,
    onDeckUpdate,
    renderMode: 'interactive' as const,
    qualCreativeFilterLabels,
    plotOtherToggleLabels,
    plotOtherMembersHeading,
  }

  // TPL-006: Runtime validation + sanitization of slide payload
  const validationResult = validateSlideData(slide)
  if (!validationResult.valid) {
    return (
      <NoDataFallback
        title={(slide as any).title || slide.type.replace(/-/g, ' ')}
        message={i18n.t('studyReport.slideCouldNotDisplay', { ns: 'app' })}
        suggestion={i18n.t('studyReport.slideRefreshSuggestion', { ns: 'app' })}
      />
    )
  }

  // Use sanitized data for rendering (truncated arrays, clamped values)
  const sanitizedSlide = validationResult.sanitizedData

  // Helper function to render slide content based on type
  const renderSlideContent = () => {
    // Registry lookup for registered components
    const Component = getSlideComponent(sanitizedSlide.type);

    if (Component) {
      return <Component {...sanitizedSlide} {...baseProps} />;
    }

    // Unknown slide type
    return (
      <NoDataFallback
        title={(slide as any).title || i18n.t('studyReport.slideUnavailable', { ns: 'app' })}
        message={i18n.t('studyReport.slideNotAvailableYet', { ns: 'app' })}
        suggestion={i18n.t('studyReport.slideRegenerateSuggestion', { ns: 'app' })}
      />
    );
  }

  // Wrap slide content with error boundary for graceful degradation
  return (
    <SlideErrorBoundary
      labels={slideErrorLabels}
      onError={(error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error(`[SlideRenderer] Error in ${slide.type}:`, error)
        }
      }}
    >
      <div className="relative w-full h-full">
        {renderSlideContent()}
      </div>
    </SlideErrorBoundary>
  )
}

export function DeckViewer({
  deck,
  onSlideChange,
  onEdit,
  editable = false,
  onDeckUpdate,
  className,
  onClose,
  experimentName,
  coverBackgroundSeed,
  embedded = false,
  studyId,
  sampleDemographics = null,
  chartSegmentationEnabled = true,
  failedExhibits = NO_FAILED_EXHIBITS,
  slideOrder = NO_SLIDE_ORDER,
  isPublicView = false,
  onRetryFailedExhibit,
  isRetryingFailedExhibit = false,
  focusSlideId = null,
  shareToken,
}: DeckViewerProps) {
  useSlideRegistryDiagnostics()
  const { t } = useTranslation(['app', 'common'])
  const { ready: registryReady, error: registryError } = useSlideRegistryLoader()
  const { resolvedTheme } = useTheme()

  const {
    isEditMode,
    toggleEditMode,
    currentDeck,
    saveStatus,
    canUndo,
    canRedo,
    undo,
    redo,
    editSlideField,
    editSlideFields,
    toggleSlideVisibility,
    revertToOriginal,
  } = useDeckEditing({
    deck,
    canEdit: editable,
    experimentId: studyId || deck?.experimentId || '',
    onDeckUpdate,
  })

  const chartSegmentStudyId = studyId || deck?.experimentId || ''
  const reportRevisionTime = currentDeck?.createdAt ? new Date(currentDeck.createdAt).getTime() : Number.NaN
  const reportRevision = Number.isFinite(reportRevisionTime) ? new Date(reportRevisionTime).toISOString() : undefined
  const [chartSegmentMeta, setChartSegmentMeta] = useState<ChartSegmentMeta | null>(null)
  const chartSegmentMetaRequestRef = useRef<{
    controller: AbortController
    requestId: number
    studyId: string
  } | null>(null)
  const chartSegmentMetaRequestIdRef = useRef(0)
  const hasSuccessfulChartSegmentMetaRef = useRef(false)
  const chartSegmentViewCacheRef = useRef(new Map<string, ChartSegmentViewCacheEntry>())
  const chartSegmentViewCacheScopeRef = useRef('')

  const clearChartSegmentViewCache = useCallback(() => {
    chartSegmentViewCacheRef.current.forEach((entry) => entry.controller.abort())
    chartSegmentViewCacheRef.current.clear()
  }, [])

  const trimChartSegmentViewCache = useCallback(() => {
    const cache = chartSegmentViewCacheRef.current
    while (cache.size > CHART_SEGMENT_VIEW_CACHE_LIMIT) {
      const inactiveEntry = Array.from(cache.entries()).find(
        ([, entry]) => entry.consumers === 0 && !entry.isAbortScheduled,
      )
      if (!inactiveEntry) {
        return
      }
      const [inactiveKey, entry] = inactiveEntry
      if (!entry.isSettled) {
        entry.controller.abort()
      }
      cache.delete(inactiveKey)
    }
  }, [])

  const refreshChartSegmentMeta = useCallback(async () => {
    if (!chartSegmentationEnabled || !chartSegmentStudyId) {
      setChartSegmentMeta({ axes: [], tagsRevision: '' })
      return
    }
    chartSegmentMetaRequestRef.current?.controller.abort()
    const controller = new AbortController()
    const requestId = chartSegmentMetaRequestIdRef.current + 1
    chartSegmentMetaRequestIdRef.current = requestId
    chartSegmentMetaRequestRef.current = {
      controller,
      requestId,
      studyId: chartSegmentStudyId,
    }
    function ownsRequest() {
      const current = chartSegmentMetaRequestRef.current
      return (
        !controller.signal.aborted &&
        current?.requestId === requestId &&
        current.studyId === chartSegmentStudyId
      )
    }

    try {
      const meta = await deckApi.getChartSegmentMeta(chartSegmentStudyId, controller.signal)
      if (!ownsRequest()) {
        return
      }
      hasSuccessfulChartSegmentMetaRef.current = true
      setChartSegmentMeta((previous) =>
        meta.axes.length === 0 ? meta : mergeChartSegmentMeta(previous, meta),
      )
      return
    } catch {
      if (!ownsRequest()) {
        return
      }
      if (hasSuccessfulChartSegmentMetaRef.current) {
        return
      }
    }
    const fallback = buildFallbackChartSegmentMeta({ sampleDemographics })
    if (fallback.axes.length > 0) {
      if (ownsRequest()) {
        setChartSegmentMeta(fallback)
      }
      return
    }
    if (ownsRequest()) {
      setChartSegmentMeta(null)
    }
  }, [chartSegmentationEnabled, chartSegmentStudyId, sampleDemographics])

  useEffect(() => {
    chartSegmentMetaRequestRef.current?.controller.abort()
    chartSegmentMetaRequestRef.current = null
    hasSuccessfulChartSegmentMetaRef.current = false
    setChartSegmentMeta(null)
    clearChartSegmentViewCache()
    chartSegmentViewCacheScopeRef.current = ''
    void refreshChartSegmentMeta()
    return () => {
      chartSegmentMetaRequestRef.current?.controller.abort()
    }
  }, [chartSegmentStudyId, reportRevision, chartSegmentationEnabled, clearChartSegmentViewCache, refreshChartSegmentMeta])

  const loadChartSegmentView = useCallback(
    async (
      slideId: string,
      request: ChartSegmentViewRequest,
      signal?: AbortSignal,
      shouldNotify = true,
    ): Promise<ChartSegmentView> => {
      if (!chartSegmentationEnabled || !chartSegmentStudyId) {
        throw new Error('Chart segmentation is unavailable')
      }
      const cacheScope = `${chartSegmentStudyId}:${reportRevision || ''}:${chartSegmentMeta?.tagsRevision || ''}`
      if (chartSegmentViewCacheScopeRef.current !== cacheScope) {
        clearChartSegmentViewCache()
        chartSegmentViewCacheScopeRef.current = cacheScope
      }
      const cacheKey = JSON.stringify({
        studyId: chartSegmentStudyId,
        slideId,
        reportRevision,
        tagsRevision: chartSegmentMeta?.tagsRevision || '',
        request: {
          axisId: request.axisId,
          levelIds: request.levelIds,
          slideType: request.slideType,
          questionId: request.questionId,
          metricId: request.metricId,
          assetId: request.assetId,
          columns: request.columns,
          rows: request.rows,
          categories: request.categories,
          ratingSemanticsVersion: request.ratingSemanticsVersion,
        },
      })
      let cacheEntry = chartSegmentViewCacheRef.current.get(cacheKey)
      if (!cacheEntry) {
        const controller = new AbortController()
        const promise = deckApi.getChartSegmentView(
          chartSegmentStudyId,
          slideId,
          { ...request, reportRevision },
          controller.signal,
        )
        const createdEntry: ChartSegmentViewCacheEntry = {
          controller,
          promise,
          consumers: 0,
          isSettled: false,
          isAbortScheduled: false,
        }
        cacheEntry = createdEntry
        chartSegmentViewCacheRef.current.set(cacheKey, createdEntry)
        void promise.catch(() => {
          if (chartSegmentViewCacheRef.current.get(cacheKey)?.promise === promise) {
            chartSegmentViewCacheRef.current.delete(cacheKey)
          }
        }).finally(() => {
          createdEntry.isSettled = true
          trimChartSegmentViewCache()
        })
      }
      const activeEntry = cacheEntry
      activeEntry.consumers += 1
      trimChartSegmentViewCache()
      let isReleased = false
      function releaseConsumer(didAbort: boolean) {
        if (isReleased) {
          return
        }
        isReleased = true
        activeEntry.consumers = Math.max(0, activeEntry.consumers - 1)
        if (
          didAbort &&
          activeEntry.consumers === 0 &&
          !activeEntry.isSettled &&
          !activeEntry.isAbortScheduled
        ) {
          activeEntry.isAbortScheduled = true
          queueMicrotask(() => {
            if (
              activeEntry.consumers === 0 &&
              !activeEntry.isSettled
            ) {
              activeEntry.controller.abort()
              if (chartSegmentViewCacheRef.current.get(cacheKey) === activeEntry) {
                chartSegmentViewCacheRef.current.delete(cacheKey)
              }
            }
            activeEntry.isAbortScheduled = false
            trimChartSegmentViewCache()
          })
          return
        }
        trimChartSegmentViewCache()
      }
      const consumerPromise = new Promise<ChartSegmentView>((resolve, reject) => {
        function handleAbort() {
          releaseConsumer(true)
          const error = new Error('canceled')
          error.name = 'CanceledError'
          reject(error)
        }
        if (signal?.aborted) {
          handleAbort()
          return
        }
        signal?.addEventListener('abort', handleAbort, { once: true })
        activeEntry.promise.then(
          (nextView) => {
            signal?.removeEventListener('abort', handleAbort)
            releaseConsumer(false)
            resolve(nextView)
          },
          (error) => {
            signal?.removeEventListener('abort', handleAbort)
            releaseConsumer(false)
            reject(error)
          },
        )
      })
      try {
        return await consumerPromise
      } catch (error) {
        if ((error as { name?: string })?.name === 'CanceledError' || signal?.aborted) {
          throw error
        }
        if (shouldNotify) {
          toast.error((error as { response?: { status?: number } })?.response?.status === 409
            ? 'The report changed. Reload it before opening a segment.'
            : 'Could not load the segment view.')
        }
        throw error
      }
    },
    [
      chartSegmentationEnabled,
      chartSegmentMeta?.tagsRevision,
      chartSegmentStudyId,
      reportRevision,
      clearChartSegmentViewCache,
      trimChartSegmentViewCache,
    ],
  )

  const getChartSegmentView = useCallback(
    (
      slideId: string,
      request: ChartSegmentViewRequest,
      signal?: AbortSignal,
    ): Promise<ChartSegmentView> => {
      return loadChartSegmentView(slideId, request, signal)
    },
    [loadChartSegmentView],
  )

  const chartSegmentLabels = useChartSegmentLabels()
  const chartSegmentValue = useMemo(
    () => ({
      studyId: chartSegmentStudyId,
      meta: chartSegmentMeta,
      refreshMeta: refreshChartSegmentMeta,
      getView: getChartSegmentView,
      labels: chartSegmentLabels,
    }),
    [chartSegmentLabels, chartSegmentMeta, chartSegmentStudyId, getChartSegmentView, refreshChartSegmentMeta],
  )

  const [showRevertConfirm, setShowRevertConfirm] = useState(false)

  const sourceSlideIndices = useMemo(() => renderableSlideIndices(currentDeck.slides), [currentDeck.slides])

  const deckForRender = useMemo(
    () => prepareDeckForRender(currentDeck, { coverBackgroundSeed }),
    [currentDeck, coverBackgroundSeed],
  )

  const visibleSlides = useMemo(() => {
    if (isEditMode) return deckForRender.slides
    return readerVisibleSlides(deckForRender.slides, deckForRender.metadata?.hiddenSlideIndices)
  }, [deckForRender.slides, deckForRender.metadata?.hiddenSlideIndices, isEditMode])

  // The pager: rendered slides plus each failed exhibit at its sequence place.
  const pagerEntries = useMemo(
    () => buildDeckPagerEntries(visibleSlides, failedExhibits, slideOrder),
    [visibleSlides, failedExhibits, slideOrder],
  )

  const [exportKind, setExportKind] = useState<null | 'pdf' | 'pptx'>(null)

  // Export the deck. We always send the PUBLISHED slide set (prepared deck with
  // hidden slides excluded, regardless of edit mode) and brand is already merged
  // into metadata by getDeck, so the file matches exactly what a viewer sees,
  // never the editor's working view. The PDF is the headless render of the
  // designed slides; the PowerPoint is an editable file of the same deck's text,
  // native charts and tables. A shared link does not send the deck: the public
  // route loads that study's published deck itself (the PowerPoint still sends
  // the brand colours this viewer resolved).
  const exportDeck = useCallback(async (kind: 'pdf' | 'pptx') => {
    if (exportKind) return
    setExportKind(kind)
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 180_000)
    const isPdf = kind === 'pdf'
    try {
      const hidden = deckForRender.metadata?.hiddenSlideIndices || []
      const publishedSlides = deckForRender.slides.filter((_, idx) => !hidden.includes(idx))
      const deckForExport = { ...deckForRender, slides: publishedSlides }
      const filename = isPdf ? deckPdfFilename(experimentName) : deckPptxFilename(experimentName)
      // The PowerPoint draws in the brand colours deck-ui resolved for this deck.
      const themeTokens = isPdf ? undefined : deckThemeTokensForExport(deckForRender.metadata)
      const themeBody = themeTokens ? { theme_tokens: themeTokens } : {}
      const body = JSON.stringify(
        shareToken ? { filename, ...themeBody } : { deck_json: deckForExport, filename, ...themeBody },
      )
      let blob: Blob
      if (shareToken) {
        blob = await publicReportBlob(shareToken, `/deck/${kind}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          signal: controller.signal,
        })
      } else {
        const response = await fetch(`/api/decks/generate-${kind}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          signal: controller.signal,
        })
        if (!response.ok) {
          if (response.status === 413) {
            toast.error('This deck is too large to export.')
          } else {
            toast.error(isPdf ? 'Could not generate the PDF. Please try again.' : t('studyReport.pptxExportFailed'))
          }
          return
        }
        blob = await response.blob()
      }
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      if (isGuestSessionExpiredError(err)) {
        return
      }
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 413) {
        toast.error('This deck is too large to export.')
        return
      }
      const timedOut = (err as Error)?.name === 'AbortError'
      if (isPdf) {
        toast.error(
          timedOut
            ? 'PDF export timed out. Please try again.'
            : 'Failed to download the PDF. Please try again.',
        )
      } else {
        toast.error(timedOut ? t('studyReport.pptxExportTimeout') : t('studyReport.pptxDownloadFailed'))
      }
    } finally {
      clearTimeout(timeout)
      setExportKind(null)
    }
  }, [deckForRender, experimentName, exportKind, shareToken, t])

  const [currentSlide, setCurrentSlide] = useState(0)
  const [slideDirection, setSlideDirection] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [hoverCardPortalContainer, setHoverCardPortalContainer] = useState<HTMLElement | null>(null)
  const hoverCardPortalRef = useRef<HTMLDivElement>(null)
  const slideViewportRef = useRef<HTMLDivElement | null>(null)
  const [slideScale, setSlideScale] = useState<number | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const baseWidth = slideDimensions.width || 1400
  const baseHeight = slideDimensions.height || 787
  const ARROW_PADDING = 144

  useEffect(() => {
    return applyDeckMetadataBranding(deckForRender.metadata, resolvedTheme)
  }, [deckForRender.metadata, resolvedTheme])

  const computeScale = useCallback((el: HTMLDivElement) => {
    const { clientWidth, clientHeight } = el
    if (!clientWidth || !clientHeight) return null
    const availableWidth = embedded ? clientWidth : Math.max(clientWidth - ARROW_PADDING, 1)
    const nextScale = Math.min(availableWidth / baseWidth, clientHeight / baseHeight)
    return Number.isFinite(nextScale) && nextScale > 0 ? nextScale : null
  }, [embedded, baseWidth, baseHeight, ARROW_PADDING])

  // Callback ref: compute scale synchronously on first mount to avoid the
  // hidden→visible flash that occurs when the ResizeObserver fires asynchronously.
  const setViewportRef = useCallback((el: HTMLDivElement | null) => {
    slideViewportRef.current = el
    if (!el) return
    const initial = computeScale(el)
    if (initial !== null) setSlideScale(initial)
  }, [computeScale])

  useEffect(() => {
    setHoverCardPortalContainer(hoverCardPortalRef.current)
  }, [])

  useEffect(() => {
    const viewport = slideViewportRef.current
    if (!viewport) return

    const updateScale = () => {
      const next = computeScale(viewport)
      if (next !== null) setSlideScale(next)
    }

    updateScale()
    const observer = new ResizeObserver(updateScale)
    observer.observe(viewport)
    return () => {
      observer.disconnect()
    }
  }, [isFullscreen, embedded, registryReady, computeScale])

  // Use refs to avoid stale closures in event handlers
  const currentSlideRef = useRef(currentSlide)
  const isTransitioningRef = useRef(isTransitioning)

  useEffect(() => {
    currentSlideRef.current = currentSlide
  }, [currentSlide])

  useEffect(() => {
    isTransitioningRef.current = isTransitioning
  }, [isTransitioning])

  // A slide move ends 500 ms after it starts; the timer dies with the viewer.
  useEffect(() => {
    if (!isTransitioning) return
    const timer = setTimeout(() => setIsTransitioning(false), 500)
    return () => clearTimeout(timer)
  }, [isTransitioning])

  const totalSlides = pagerEntries.length
  const safeCurrentSlide = totalSlides > 0
    ? Math.min(currentSlide, totalSlides - 1)
    : 0
  const currentEntry = pagerEntries[safeCurrentSlide]
  // The rendered slide on screen; none while a failed exhibit's placeholder shows.
  const currentSlideData = currentEntry?.kind === 'slide' ? currentEntry.slide : null

  // Open at the focused item once its entry is in the pager; a later pager
  // change (a deck landing, an edit) never pulls the reader back.
  const appliedFocusRef = useRef<string | null>(null)
  useEffect(() => {
    if (!focusSlideId || appliedFocusRef.current === focusSlideId) return
    const index = deckPagerIndexOf(pagerEntries, focusSlideId)
    if (index < 0) return
    appliedFocusRef.current = focusSlideId
    setSlideDirection(index > currentSlideRef.current ? 1 : -1)
    setCurrentSlide(index)
  }, [focusSlideId, pagerEntries])

  useEffect(() => {
    if (currentSlide >= totalSlides && totalSlides > 0) {
      setCurrentSlide(totalSlides - 1)
    }
  }, [totalSlides, currentSlide])

  const goToNext = useCallback(() => {
    if (currentSlideRef.current < totalSlides - 1 && !isTransitioningRef.current) {
      setIsTransitioning(true)
      setSlideDirection(1)
      setCurrentSlide((prev) => prev + 1)
    }
  }, [totalSlides])

  const goToPrevious = useCallback(() => {
    if (currentSlideRef.current > 0 && !isTransitioningRef.current) {
      setIsTransitioning(true)
      setSlideDirection(-1)
      setCurrentSlide((prev) => prev - 1)
    }
  }, [])

  const goToSlide = useCallback((index: number) => {
    if (index >= 0 && index < totalSlides && !isTransitioningRef.current) {
      setIsTransitioning(true)
      setSlideDirection(index > currentSlideRef.current ? 1 : -1)
      setCurrentSlide(index)
    }
  }, [totalSlides])

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen()
        setIsFullscreen(true)
      } catch { /* browser refused */ }
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false)
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) {
        return
      }
      const target = e.target instanceof Element ? e.target : null
      if (
        target?.closest(
          'input, textarea, select, [contenteditable="true"], [data-chart-segment-control="true"]',
        )
      ) {
        return
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        if (!isEditMode) return
        e.preventDefault()
        undo()
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
        if (!isEditMode) return
        e.preventDefault()
        redo()
        return
      }

      if ((e.target as HTMLElement)?.isContentEditable) return

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goToPrevious()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        goToNext()
      } else if (e.key === 'Escape') {
        if (!isFullscreen && onClose) {
          onClose()
        }
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goToNext, goToPrevious, isFullscreen, toggleFullscreen, onClose, undo, redo, isEditMode])

  useEffect(() => {
    onSlideChange?.(currentSlide)
  }, [currentSlide, onSlideChange])

  const handleEdit = (field: string, value: unknown) => {
    if (!currentSlideData) return
    if (isEditMode) {
      const originalIndex = deckForRender.slides.indexOf(currentSlideData)
      if (originalIndex >= 0) editSlideField(sourceSlideIndices[originalIndex], field, String(value))
    }
    const renderIndex = deckForRender.slides.indexOf(currentSlideData)
    if (renderIndex >= 0) onEdit?.(sourceSlideIndices[renderIndex], field, value)
  }

  // One user edit that writes several fields: one history entry (one undo step).
  const handleEditFields = (edits: Array<SlideFieldEdit>) => {
    if (!currentSlideData) return
    const renderIndex = deckForRender.slides.indexOf(currentSlideData)
    if (renderIndex < 0) return
    if (isEditMode) editSlideFields(sourceSlideIndices[renderIndex], edits)
    for (const edit of edits) {
      onEdit?.(sourceSlideIndices[renderIndex], edit.field, edit.value)
    }
  }

  const handleToggleVisibility = () => {
    if (!currentSlideData) return
    const originalIndex = deckForRender.slides.indexOf(currentSlideData)
    if (originalIndex >= 0) {
      toggleSlideVisibility(sourceSlideIndices[originalIndex])
    }
  }

  const isCurrentSlideHidden = (() => {
    if (!currentSlideData) return false
    const originalIndex = deckForRender.slides.indexOf(currentSlideData)
    return (deckForRender.metadata?.hiddenSlideIndices || []).includes(originalIndex)
  })()


  // The deck artifact's own status, read through the report view state.
  const deckState = reportViewState(null, {
    status: deck.status,
    hasSlides: Array.isArray(deck.slides) && deck.slides.length > 0,
    isFetching: false,
  })

  if (deckState.kind === 'deck_generating') {
    return (
      <div className={cn("w-full aspect-video bg-muted rounded-2xl flex items-center justify-center", className)}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-muted-foreground animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-sans">{t('studyReport.generatingDeckEllipsis')}</p>
        </div>
      </div>
    )
  }

  if (deckState.kind === 'deck_failed') {
    return (
      <div className={cn("w-full aspect-video bg-destructive/10 rounded-2xl flex items-center justify-center border border-destructive/30", className)}>
        <div className="text-center">
          <p className="text-destructive font-sans font-medium mb-2">{t('studyReport.generateDeckFailed')}</p>
          <p className="text-destructive/90 font-sans text-sm">{t('studyReport.tryOrSupport')}</p>
        </div>
      </div>
    )
  }

  if (registryError) {
    return (
      <div className={cn("w-full aspect-video bg-destructive/10 rounded-2xl flex items-center justify-center border border-destructive/30", className)}>
        <div className="text-center">
          <p className="text-destructive font-sans font-medium mb-2">{t('studyReport.slideComponentsFailed')}</p>
          <p className="text-destructive/90 font-sans text-sm">{registryError}</p>
        </div>
      </div>
    )
  }

  if (!registryReady) {
    return (
      <div className={cn("w-full aspect-video bg-muted rounded-2xl flex items-center justify-center", className)}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-muted-foreground animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-sans">{t('studyReport.loadingSlides')}</p>
        </div>
      </div>
    )
  }

  if (totalSlides === 0) {
    return (
      <div className={cn("w-full aspect-video bg-muted rounded-2xl flex items-center justify-center", className)}>
        <p className="text-muted-foreground font-sans">{t('studyReport.noSlides')}</p>
      </div>
    )
  }

  // Rendered pixel size of the slide — used to size the bg-muted shell exactly.
  const renderedWidth = slideScale !== null ? Math.round(baseWidth * slideScale) : null
  const renderedHeight = slideScale !== null ? Math.round(baseHeight * slideScale) : null

  return (
      <div
        className={cn(
          "relative isolate w-full flex flex-col",
          isFullscreen ? "fixed inset-0 z-50 rounded-none h-full bg-neutral-950 overflow-hidden" : "",
          !isFullscreen && embedded && "h-full",
          !isFullscreen && !embedded && !className?.includes('h-') && "aspect-video bg-muted rounded-2xl",
          className
        )}
      tabIndex={0}
    >
      {/* Slide area — fills remaining space above bottom bar */}
      <div className="relative flex-1 min-h-0 group/nav">
        {/* Viewport — no horizontal padding in embedded (no arrows); px-[72px] in standalone */}
        <div
          ref={setViewportRef}
          data-testid="deck-viewer-viewport"
          className={cn(
            "relative w-full h-full flex items-center justify-center overflow-hidden",
            !embedded && !isFullscreen && "px-[72px]",
          )}
        >
          <div
            ref={hoverCardPortalRef}
            className="absolute inset-0 z-overlay pointer-events-none"
            aria-hidden="true"
          />
          <AnimatePresence initial={false} mode="sync" custom={slideDirection}>
            <motion.div
              key={safeCurrentSlide}
              custom={slideDirection}
              initial="enter"
              animate="center"
              exit="exit"
              variants={{
                enter: (dir: number) => ({ opacity: 0, x: dir * 40, scale: 0.97 }),
                center: { opacity: 1, x: 0, scale: 1 },
                exit: (dir: number) => ({ opacity: 0, x: dir * -40, scale: 0.97, position: 'absolute' as any }),
              }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              className="w-full h-full flex items-center justify-center pointer-events-auto"
            >
              <HoverCardPortalProvider container={hoverCardPortalContainer}>
                <div
                  className={cn(
                    "relative overflow-hidden",
                    !isFullscreen && "bg-muted rounded-2xl border border-border/60",
                  )}
                  style={
                    renderedWidth !== null && renderedHeight !== null
                      ? { width: renderedWidth, height: renderedHeight, flexShrink: 0 }
                      : undefined
                  }
                >
                <div
                  style={{
                    width: slideDimensions.width || 1400,
                    height: slideDimensions.height || 787,
                    transform: `scale(${slideScale ?? 1})`,
                    transformOrigin: 'top left',
                    visibility: slideScale === null ? 'hidden' : 'visible',
                  }}
                >
                  {currentEntry?.kind === 'failed_exhibit' ? (
                    <FailedSlidePlaceholder
                      exhibit={currentEntry.exhibit}
                      isPublicView={isPublicView}
                      // No regeneration while unsaved deck edits are open.
                      onRetry={isEditMode ? undefined : onRetryFailedExhibit}
                      isRetrying={isRetryingFailedExhibit}
                    />
                  ) : currentSlideData ? (
                  <ChartSegmentProvider value={chartSegmentValue}>
                  <SlideRenderer
                    slide={currentSlideData}
                    isActive={true}
                    onEdit={handleEdit}
                    onEditFields={handleEditFields}
                    editable={isEditMode}
                    experimentId={deck?.experimentId}
                    deck={deckForRender}
                    onDeckUpdate={onDeckUpdate}
                  />
                  </ChartSegmentProvider>
                  ) : null}
                  {isEditMode && isCurrentSlideHidden && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-lg pointer-events-none">
                      <span className="text-white/80 text-lg font-medium px-4 py-2 bg-black/50 rounded-full">
                        Hidden in view mode
                      </span>
                    </div>
                  )}
                </div>
                </div>
              </HoverCardPortalProvider>
            </motion.div>
          </AnimatePresence>
        </div>

        {!embedded && (
          <>
            {/* Left arrow — revealed on hover of the slide, hit area does not cover the plot */}
            <div className="pointer-events-none group/left absolute left-0 top-0 bottom-0 w-20 z-20">
              <button
                type="button"
                aria-label={t('chromeA11y.prevSlide')}
                data-testid="deck-viewer-prev"
                onClick={goToPrevious}
                disabled={safeCurrentSlide === 0}
                className={cn(
                  "pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/80 backdrop-blur-md text-foreground border border-black/10",
                  "opacity-0 -translate-x-4 transition-all duration-300 ease-out",
                  "hover:bg-white/95 disabled:cursor-not-allowed",
                  safeCurrentSlide > 0 && "group-hover/nav:pointer-events-auto group-hover/nav:opacity-100 group-hover/nav:translate-x-0 focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:translate-x-0",
                )}
              >
                <ChevronLeft className="w-6 h-6" strokeWidth={2} />
              </button>
            </div>

            {/* Right arrow — revealed on hover of the slide, hit area does not cover the plot */}
            <div className="pointer-events-none group/right absolute right-0 top-0 bottom-0 w-20 z-20">
              <button
                type="button"
                aria-label={t('chromeA11y.nextSlide')}
                data-testid="deck-viewer-next"
                onClick={goToNext}
                disabled={safeCurrentSlide === totalSlides - 1}
                className={cn(
                  "pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/80 backdrop-blur-md text-foreground border border-black/10",
                  "opacity-0 translate-x-4 transition-all duration-300 ease-out",
                  "hover:bg-white/95 disabled:cursor-not-allowed",
                  safeCurrentSlide < totalSlides - 1 && "group-hover/nav:pointer-events-auto group-hover/nav:opacity-100 group-hover/nav:translate-x-0 focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:translate-x-0",
                )}
              >
                <ChevronRight className="w-6 h-6" strokeWidth={2} />
              </button>
            </div>
          </>
        )}

        {/* Back/Close Button — revealed on hover over top-left zone */}
        {onClose && (
          <div className="group/back absolute top-0 left-0 w-56 h-20 z-20">
            <button
              onClick={onClose}
              data-testid="deck-viewer-back"
              className="absolute top-6 left-6 flex items-center rounded-full bg-white/80 backdrop-blur-md text-foreground hover:bg-white/95 border border-black/10 p-2.5 opacity-0 -translate-x-4 group-hover/back:opacity-100 group-hover/back:translate-x-0 transition-all duration-300 ease-out cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              <span className="max-w-0 overflow-hidden opacity-0 group-hover/back:max-w-32 group-hover/back:opacity-100 transition-all duration-300 ease-out whitespace-nowrap text-sm ml-0 group-hover/back:ml-2">
                {t('studyReport.backToReport')}
              </span>
            </button>
          </div>
        )}

        {/* Revert Confirmation Dialog */}
        {showRevertConfirm && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-2xl">
            <div className="bg-white rounded-xl p-6 max-w-sm shadow-2xl mx-4">
              <h3 className="text-lg font-semibold text-zinc-900 mb-2">Revert to original?</h3>
              <p className="text-sm text-zinc-600 mb-5">
                This will discard all edits and restore the AI-generated deck. This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  data-testid="deck-revert-cancel"
                  onClick={() => setShowRevertConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="deck-revert-confirm"
                  onClick={async () => {
                    setShowRevertConfirm(false)
                    await revertToOriginal()
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Revert
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Keyboard Hints */}
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ delay: 3, duration: 0.5 }}
          className="absolute bottom-4 right-4 text-muted-foreground text-xs font-sans pointer-events-none"
        >
          Use ← → arrows to navigate • Press F for fullscreen{onClose ? ' • ESC to close' : ''}
        </motion.div>

        {/* Exit fullscreen button — visible only in fullscreen mode */}
        {isFullscreen && (
          <button
            onClick={toggleFullscreen}
            className="absolute top-4 right-4 z-30 p-2.5 rounded-full bg-white/80 backdrop-blur-md text-foreground border border-black/10 hover:bg-white/95 transition-colors shadow-sm"
            title="Exit fullscreen (F)"
          >
            <Minimize2 className="w-5 h-5" strokeWidth={1.5} />
          </button>
        )}
      </div>

      {/* Bottom Controls — below the slide, hidden in fullscreen */}
      {!isFullscreen && <div className="shrink-0 px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Progress Dots */}
          <div className="flex items-center gap-2">
            {pagerEntries.map((entry, index) => {
              const isFailed = entry.kind === 'failed_exhibit'
              const isCurrent = index === safeCurrentSlide
              return (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  aria-label={
                    isFailed
                      ? t('studyReport.failedSlide.dotLabel', { number: index + 1 })
                      : t('studyReport.goToSlide', { number: index + 1 })
                  }
                  data-testid={`deck-viewer-progress-${index}`}
                  aria-current={isCurrent ? 'step' : undefined}
                  data-failed={isFailed ? 'true' : undefined}
                  className={cn(
                    "transition-all duration-300 h-2 rounded-full",
                    isCurrent ? "w-8" : "w-2",
                    isFailed
                      ? isCurrent
                        ? "bg-destructive"
                        : "bg-destructive/70 hover:bg-destructive"
                      : isCurrent
                        ? "bg-foreground/60"
                        : "bg-foreground/20 hover:bg-foreground/40"
                  )}
                />
              )
            })}
          </div>

          {/* Control Buttons */}
          <div className="flex items-center gap-2">
            {editable && !isEditMode && (
              <button
                data-testid="deck-edit-toggle"
                onClick={toggleEditMode}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-zinc-700 text-sm font-medium border border-zinc-200 hover:bg-zinc-50 transition-colors shadow-sm"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
            {editable && isEditMode && (
              <>
                <span className={cn(
                  "text-xs px-2 py-1 rounded-full",
                  saveStatus === 'saving' && "bg-amber-100 text-amber-700",
                  saveStatus === 'saved' && "bg-green-100 text-green-700",
                  saveStatus === 'error' && "bg-red-100 text-red-700",
                  saveStatus === 'idle' && "bg-zinc-100 text-zinc-500",
                )}>
                  {saveStatus === 'saving' && 'Saving...'}
                  {saveStatus === 'saved' && 'Saved'}
                  {saveStatus === 'error' && 'Save failed'}
                  {saveStatus === 'idle' && 'Editing'}
                </span>
                <button
                  data-testid="deck-edit-revert"
                  onClick={() => setShowRevertConfirm(true)}
                  className="p-1.5 rounded-full bg-white text-zinc-700 border border-zinc-200 hover:bg-red-50 hover:text-red-600 transition-colors shadow-sm"
                  title="Revert to original"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  data-testid="deck-edit-toggle-visibility"
                  onClick={handleToggleVisibility}
                  className={cn(
                    "p-1.5 rounded-full border border-zinc-200 transition-colors shadow-sm",
                    isCurrentSlideHidden
                      ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                      : "bg-white text-zinc-700 hover:bg-zinc-50"
                  )}
                  title={isCurrentSlideHidden ? 'Show slide' : 'Hide slide'}
                >
                  {isCurrentSlideHidden ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
                <button
                  data-testid="deck-edit-undo"
                  onClick={undo}
                  disabled={!canUndo}
                  className="p-1.5 rounded-full bg-white text-zinc-700 border border-zinc-200 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
                  title="Undo (Cmd+Z)"
                >
                  <Undo2 className="w-4 h-4" />
                </button>
                <button
                  data-testid="deck-edit-redo"
                  onClick={redo}
                  disabled={!canRedo}
                  className="p-1.5 rounded-full bg-white text-zinc-700 border border-zinc-200 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
                  title="Redo (Cmd+Shift+Z)"
                >
                  <Redo2 className="w-4 h-4" />
                </button>
                <button
                  data-testid="deck-edit-done"
                  onClick={toggleEditMode}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
                >
                  <Check className="w-3.5 h-3.5" />
                  Done
                </button>
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => exportDeck('pdf')}
              disabled={exportKind !== null}
              data-testid="deck-export-pdf"
              title="Download as PDF"
              aria-label="Download as PDF"
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
            >
              {exportKind === 'pdf' ? (
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
              ) : (
                <Download className="w-5 h-5" strokeWidth={1.5} />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => exportDeck('pptx')}
              disabled={exportKind !== null}
              data-testid="deck-export-pptx"
              title={t('studyReport.downloadPptx')}
              aria-label={t('studyReport.downloadPptx')}
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-full gap-1.5 px-2.5"
            >
              {exportKind === 'pptx' ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Presentation className="w-4 h-4" strokeWidth={1.5} />
              )}
              {t('studyReport.pptx')}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
            >
              {isFullscreen ? (
                <Minimize2 className="w-5 h-5" strokeWidth={1.5} />
              ) : (
                <Maximize2 className="w-5 h-5" strokeWidth={1.5} />
              )}
            </Button>
          </div>
        </div>
      </div>}
    </div>
  )
}

export function DeckViewerSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("w-full aspect-video bg-muted rounded-2xl animate-pulse", className)}>
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
      </div>
    </div>
  )
}
